import { Stack } from "@mui/material";
import { CircularChart, type Messages } from "src/components/charts/donut-chart/CircularChart";
import { SenecChartLegend } from "src/components/monitoring/senec-chart-legend/SenecChartLegend";
import { formatLegendValue } from "src/components/monitoring/chartUtils";
import { type DonutChartData } from "src/components/charts/donut-chart/useUpdateChartData";

export interface DonutChartProps {
    data: DonutChartData[];
    isLoading?: boolean;
    onDataClick?: (id: string) => void;
    legendPosition?: "bottom" | "right";
    messages?: Messages;
    locale?: Intl.Locale;
    showLegend?: boolean;
    size?: number;
    innerRadius?: number;
}

const getColumnCount = (legendPosition: "bottom" | "right", itemCount = 0) => {
    if (legendPosition === "bottom") return 8;
    if (itemCount > 6) return 2;
    return 1;
};

export const DonutChart = ({
    data,
    isLoading,
    locale = new Intl.Locale("de"),
    onDataClick,
    messages,
    legendPosition = "bottom",
    showLegend = true,
    size = 212,
    innerRadius = 0.86,
}: DonutChartProps) => {
    const columns = getColumnCount(legendPosition, data.length);

    const totalValue = data.reduce((acc, curr) => acc + curr.value, 0);
    const unit = data[0]?.unit;
    const totalValueFormatted = unit
        ? `${formatLegendValue(locale.toString(), totalValue, false, true, data[0].unit)} ${unit}`
        : totalValue;

    const chartLegendItems = data.map((d) => ({
        id: d.id,
        translation: d.label,
        color: d.color,
        legendValue: d.value,
        dataSeriesUnit: d.unit,
        // If we define a unit we let the legend process the value
        skipLegendValueProcessing: !d.unit,
    }));

    return (
        <Stack
            spacing={2}
            direction={legendPosition === "bottom" ? "column" : "row"}
            alignItems={"center"}
            justifyContent="center"
            display={"inline-flex"}
        >
            <CircularChart
                data={data}
                isLoading={isLoading}
                onDataClick={onDataClick}
                messages={messages}
                total={totalValueFormatted}
                size={size}
                innerRadius={innerRadius}
            />
            {showLegend && (
                <SenecChartLegend
                    chartLegendItems={chartLegendItems}
                    columns={columns}
                    selectedLegendTypes={data.map((d) => d.id)}
                    toggleSelectedLegendTypes={onDataClick || undefined}
                    isLoading={isLoading}
                    locale={locale}
                    sx={{ width: "auto" }}
                    direction={legendPosition === "bottom" ? "row" : "column"}
                />
            )}
        </Stack>
    );
};
